import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import axios from 'axios'
import {
  Upload,
  Link as LinkIcon,
  Send,
  Image as ImageIcon,
  Info
} from 'lucide-react';

// Shared dark-theme admin UI styles (site palette: #252525 / #b7a78b)
const ui = {
  card: "bg-[#1d1d1d] rounded-xl border border-[#484540] p-6 md:p-8 mb-8 shadow-lg",
  title: "text-xl font-semibold mb-1 capitalize text-[#e7ddd3] tracking-wide flex items-center gap-2",
  subtitle: "text-sm text-[#8a8175] mb-6",
  label: "block text-xs font-medium text-[#8a8175] uppercase tracking-wide mb-1.5",
  input:
    "w-full bg-[#252525] border border-[#484540] rounded-lg px-4 py-2.5 text-sm text-[#e4e4e3] placeholder-[#6f675c] focus:outline-none focus:ring-2 focus:ring-[#b7a78b] focus:border-transparent transition",
  select:
    "w-full sm:w-96 bg-[#252525] border border-[#484540] rounded-lg px-4 py-2.5 text-sm text-[#e4e4e3] focus:outline-none focus:ring-2 focus:ring-[#b7a78b] focus:border-transparent transition cursor-pointer",
  primaryBtn:
    "inline-flex items-center justify-center gap-2 bg-[#b7a78b] hover:bg-[#d0c3a6] text-[#252525] text-sm font-semibold px-8 py-3 rounded-lg uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed",
  deleteBtn:
    "bg-red-600/90 hover:bg-red-600 text-white text-xs font-semibold uppercase px-4 py-2 rounded-md transition-colors",
  editBtn:
    "bg-[#252525] border border-[#484540] hover:border-[#b7a78b] text-[#e4e4e3] text-xs font-semibold uppercase px-4 py-2 rounded-md transition-colors",
  dropzone:
    "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-[#484540] bg-[#252525] px-6 py-8 text-center transition hover:border-[#b7a78b]",
  fileBtn:
    "block w-full text-sm text-[#cdc2ad] file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#b7a78b] file:text-[#252525] file:font-semibold file:cursor-pointer cursor-pointer",
  th: "px-6 py-3 text-left text-xs font-semibold text-[#8a8175] uppercase tracking-wider bg-[#252525]",
  td: "px-6 py-4 whitespace-nowrap text-sm text-[#cdc2ad]",
  tr: "hover:bg-[#252525] transition-colors",
};

const ExhibitionUploadForm = () => {
  const [exhibitions, setExhibitions] = useState([]);
  const [selectedExhibitionsId, setSelectedExhibitionsId] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);

  // slide upload form
  const [files, setFiles] = useState([]);
  const [text1, setText1] = useState('');
  const [text2, setText2] = useState('');
  const [link, setLink] = useState('');
  const [previewImages, setPreviewImages] = useState([]);
  const [uploadStatus, setUploadStatus] = useState({ loading: false, success: false, error: null });

  // uploaded slides list + inline edit
  const [slides, setSlides] = useState([]);
  const [editingSlideId, setEditingSlideId] = useState(null);
  const [slideEdit, setSlideEdit] = useState({ text1: '', text2: '', link: '' });
  const [slideEditFile, setSlideEditFile] = useState(null);
  const [slideEditPreview, setSlideEditPreview] = useState(null);
  const [savingSlide, setSavingSlide] = useState(false);

  // exhibition details (title & kind, showings, overview, credits)
  const [info, setInfo] = useState({ titleKind: '', showings: [{ location: '', date: '' }], overview: '', credits: '' });
  const [infoDocId, setInfoDocId] = useState(null);
  const [savingInfo, setSavingInfo] = useState(false);
  const [allInfos, setAllInfos] = useState([]);

  useEffect(() => {
    fetchData();
    fetchAllSlides();
    fetchAllInfos();
  }, []);

  const fetchData = async () => {
    try {
      const [exhibitionsRes, portfoliosRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/exhibitions`),
        fetch(`${process.env.REACT_APP_API_URL}/exhibitupload`)
      ]);

      if (exhibitionsRes.ok && portfoliosRes.ok) {
        const exhibitionsData = await exhibitionsRes.json();
        const portfolios = await portfoliosRes.json();

        const enhancedPortfolios = portfolios.map((portfolio) => {
          const matchingExhibition = exhibitionsData.find(
            (exhibition) => exhibition._id === portfolio.exhibitonBrand
          );
          return {
            ...portfolio,
            exhibitionName: matchingExhibition ? matchingExhibition.name : 'Unknown Exhibition'
          };
        });

        setExhibitions(exhibitionsData);
        setUploadedImages(enhancedPortfolios);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchUploadedImages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitupload`);
      const data = await response.json();
      if (response.ok) setUploadedImages(data);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  // ── Exhibition details (title & kind, showings, overview, credits) ──────
  const emptyInfo = { titleKind: '', showings: [{ location: '', date: '' }], overview: '', credits: '' };

  // Every exhibition's details (for the list at the bottom of the card)
  const fetchAllInfos = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/exhibition-infos`);
      setAllInfos(res.ok ? await res.json() : []);
    } catch (error) {
      console.error('Error fetching exhibition details:', error);
      setAllInfos([]);
    }
  };

  // Load a details record from the list into the form (and select its exhibition)
  const editInfoFromList = (i) => {
    setSelectedExhibitionsId(i.exhibitionId || '');
    setInfo({
      titleKind: i.titleKind || '',
      showings: i.showings?.length
        ? i.showings.map((s) => ({ location: s.location || '', date: s.date || '' }))
        : [{ location: '', date: '' }],
      overview: i.overview || '',
      credits: i.credits || '',
    });
    setInfoDocId(i._id);
  };

  const deleteInfoById = async (id) => {
    if (!window.confirm('Delete the details for this exhibition?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/exhibition-info/${id}`);
      if (infoDocId === id) {
        setInfo(emptyInfo);
        setInfoDocId(null);
      }
      fetchAllInfos();
    } catch (error) {
      alert('Failed to delete exhibition details.');
    }
  };

  const fetchInfo = async (exhibitionId) => {
    if (!exhibitionId) {
      setInfo(emptyInfo);
      setInfoDocId(null);
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/exhibition-info/${exhibitionId}`);
      if (res.ok) {
        const data = await res.json();
        setInfo({
          titleKind: data.titleKind || '',
          // always leave one blank row to type into
          showings: data.showings?.length ? data.showings.map((s) => ({ location: s.location || '', date: s.date || '' })) : [{ location: '', date: '' }],
          overview: data.overview || '',
          credits: data.credits || '',
        });
        setInfoDocId(data._id || null);
      } else {
        // 404 = no details saved for this exhibition yet
        setInfo(emptyInfo);
        setInfoDocId(null);
      }
    } catch (error) {
      console.error('Error fetching exhibition details:', error);
    }
  };

  const updateShowing = (index, field, value) => {
    setInfo((p) => {
      const showings = p.showings.map((s, i) => (i === index ? { ...s, [field]: value } : s));
      return { ...p, showings };
    });
  };

  const addShowing = () => setInfo((p) => ({ ...p, showings: [...p.showings, { location: '', date: '' }] }));

  const removeShowing = (index) =>
    setInfo((p) => {
      const showings = p.showings.filter((_, i) => i !== index);
      return { ...p, showings: showings.length ? showings : [{ location: '', date: '' }] };
    });

  const saveInfo = async () => {
    if (!selectedExhibitionsId) {
      alert('Please select an exhibition.');
      return;
    }
    setSavingInfo(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/exhibition-info`, {
        exhibitionId: selectedExhibitionsId,
        ...info,
      });
      alert('Exhibition details saved.');
      fetchInfo(selectedExhibitionsId);
      fetchAllInfos();
    } catch (error) {
      alert('Failed to save exhibition details.');
    } finally {
      setSavingInfo(false);
    }
  };

  const deleteInfo = async () => {
    if (!infoDocId) return;
    if (!window.confirm('Delete the details for this exhibition?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/exhibition-info/${infoDocId}`);
      alert('Exhibition details deleted.');
      setInfo(emptyInfo);
      setInfoDocId(null);
      fetchAllInfos();
    } catch (error) {
      alert('Failed to delete exhibition details.');
    }
  };

  // ── Gallery images ──────────────────────────────────────────────────
  const handleImageChange = (event) => {
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    const selected = Array.from(event.target.files);
    setImages(selected);
    setImagePreviews(selected.map((file) => URL.createObjectURL(file)));
  };

  const handleUpload = async () => {
    if (!selectedExhibitionsId) {
      alert('Please select an exhibition');
      return;
    }
    if (images.length === 0) {
      alert('Please select at least one image to upload');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      Array.from(images).forEach((image) => formData.append('images', image));

      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitupload/${selectedExhibitionsId}`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        alert('Images uploaded successfully');
        imagePreviews.forEach((url) => URL.revokeObjectURL(url));
        setImages([]);
        setImagePreviews([]);
        fetchUploadedImages();
      } else {
        console.error('Error uploading images:', response.statusText);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExhibition = async (exhibitionId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitupload/${exhibitionId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setUploadedImages(uploadedImages.filter((image) => image.id !== exhibitionId));
        alert('Image has been deleted successfully');
      } else {
        const errorData = await response.json();
        console.error('Error deleting Image:', errorData);
      }
    } catch (error) {
      console.error('Error deleting Image:', error);
    }
  };

  // ── Slides ──────────────────────────────────────────────────────────
  // Fetch every slide across all exhibitions (grouped in the UI by exhibition)
  const fetchAllSlides = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitslide`);
      if (response.ok) {
        setSlides(await response.json());
      } else {
        setSlides([]);
      }
    } catch (error) {
      console.error('Error fetching slides:', error);
      setSlides([]);
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
    setPreviewImages(selectedFiles.map((file) => URL.createObjectURL(file)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedExhibitionsId || files.length === 0) {
      setUploadStatus({ loading: false, success: false, error: 'Please select an exhibition and upload images' });
      return;
    }

    const formData = new FormData();
    formData.append('text1', text1);
    formData.append('text2', text2);
    formData.append('link', link);
    files.forEach((file) => formData.append('images', file));

    try {
      setUploadStatus({ loading: true, success: false, error: null });

      await axios.post(`${process.env.REACT_APP_API_URL}/exhibitslide/${selectedExhibitionsId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadStatus({ loading: false, success: true, error: null });
      setFiles([]);
      setText1('');
      setText2('');
      setLink('');
      setPreviewImages([]);
      fetchAllSlides(); // refresh the slide list
    } catch (error) {
      setUploadStatus({
        loading: false,
        success: false,
        error: error.response?.data?.error || 'Upload failed'
      });
    }
  };

  const startEditSlide = (slide) => {
    if (slideEditPreview) URL.revokeObjectURL(slideEditPreview);
    setEditingSlideId(slide._id);
    setSlideEdit({ text1: slide.text1 || '', text2: slide.text2 || '', link: slide.link || '' });
    setSlideEditFile(null);
    setSlideEditPreview(null);
  };

  const cancelEditSlide = () => {
    if (slideEditPreview) URL.revokeObjectURL(slideEditPreview);
    setEditingSlideId(null);
    setSlideEdit({ text1: '', text2: '', link: '' });
    setSlideEditFile(null);
    setSlideEditPreview(null);
  };

  const handleSlideEditFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (slideEditPreview) URL.revokeObjectURL(slideEditPreview);
    setSlideEditFile(file);
    setSlideEditPreview(URL.createObjectURL(file));
  };

  const handleSaveSlide = async (id) => {
    try {
      setSavingSlide(true);
      const formData = new FormData();
      formData.append('text1', slideEdit.text1);
      formData.append('text2', slideEdit.text2);
      formData.append('link', slideEdit.link);
      if (slideEditFile) formData.append('image', slideEditFile);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitslide/${id}`, {
        method: 'PUT',
        body: formData,
      });

      if (response.ok) {
        alert('Slide updated successfully');
        cancelEditSlide();
        fetchAllSlides();
      } else {
        alert('Failed to update slide');
      }
    } catch (error) {
      console.error('Error updating slide:', error);
      alert('Failed to update slide');
    } finally {
      setSavingSlide(false);
    }
  };

  const handleDeleteSlide = async (id) => {
    if (!window.confirm('Delete this slide?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitslide/${id}`, { method: 'DELETE' });
      if (response.ok) {
        setSlides((prev) => prev.filter((s) => s._id !== id));
        alert('Slide deleted successfully');
      } else {
        alert('Failed to delete slide');
      }
    } catch (error) {
      console.error('Error deleting slide:', error);
      alert('Failed to delete slide');
    }
  };

  // Shared exhibition picker — selecting one loads its details
  const handleExhibitionsSelect = (event) => {
    const id = event.target.value;
    setSelectedExhibitionsId(id);
    fetchInfo(id);
    cancelEditSlide();
  };

  const exhibitionSelect = (id = 'exhibition-select') => (
    <select
      id={id}
      className={ui.select}
      value={selectedExhibitionsId}
      onChange={handleExhibitionsSelect}
    >
      <option value="">Select an exhibition</option>
      {exhibitions.map((exhibition) => (
        <option key={exhibition._id} value={exhibition._id}>
          {exhibition.name}
        </option>
      ))}
    </select>
  );

  return (
    <div className="flex min-h-screen bg-[#252525]" style={{ fontFamily: "Muli" }}>
      <Sidebar />
      <div className="flex-1 bg-[#252525] min-h-screen pb-16">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#484540] bg-[#1d1d1d] px-6 py-4">
          <p className="text-sm font-medium text-[#8a8175]">Admin Dashboard</p>
          <span className="text-sm font-semibold text-[#e7ddd3]">Welcome, Admin</span>
        </div>

        <div className="mx-auto max-w-5xl px-4 md:px-8 py-8">
          {/* Page heading */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-[#e7ddd3]">Exhibitions</h1>
            <p className="mt-1 text-sm text-[#8a8175]">Manage exhibition slides, write-ups, and gallery images.</p>
          </div>

          {/* Shared exhibition picker */}
          <div className={ui.card}>
            <h2 className={ui.title}>Selected Exhibition</h2>
            <p className={ui.subtitle}>Pick an exhibition to manage its slides, write-up, and images.</p>
            {exhibitionSelect('shared-exhibition')}
          </div>

          {/* ── EXHIBITION SLIDES ──────────────────────────────────────── */}
          <div className={ui.card}>
            <h2 className={ui.title}><Upload className="h-5 w-5 text-[#b7a78b]" /> Exhibition Slides</h2>
            <p className={ui.subtitle}>Upload the video/preview slides shown at the top of the exhibition page.</p>

            <form onSubmit={handleSubmit}>
              <div className="grid gap-x-4 sm:grid-cols-2 max-w-3xl">
                <div>
                  <label className={ui.label}>First text</label>
                  <input type="text" value={text1} onChange={(e) => setText1(e.target.value)} placeholder="First text line" className={`${ui.input} mb-4`} />
                </div>
                <div>
                  <label className={ui.label}>Second text</label>
                  <input type="text" value={text2} onChange={(e) => setText2(e.target.value)} placeholder="Second text line" className={`${ui.input} mb-4`} />
                </div>
                <div className="sm:col-span-2">
                  <label className={`${ui.label} flex items-center gap-1`}><LinkIcon className="h-3.5 w-3.5" /> Link</label>
                  <input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Optional video link" className={`${ui.input} mb-4`} />
                </div>
              </div>

              <label htmlFor="slide-files" className={`${ui.dropzone} mb-4`}>
                <ImageIcon className="mb-2 h-8 w-8 text-[#8a8175]" />
                <span className="text-sm font-semibold text-[#cdc2ad]">Click to choose slide images</span>
                <span className="mt-1 text-xs text-[#6f675c]">You can select multiple files</span>
                <input id="slide-files" type="file" multiple accept="image/*" onChange={handleFileChange} className="hidden" />
              </label>

              {previewImages.length > 0 && (
                <div className="mb-4 grid grid-cols-3 gap-4">
                  {previewImages.map((preview, index) => (
                    <img key={index} src={preview} alt={`Preview ${index + 1}`} className="h-32 w-full rounded-lg object-cover ring-1 ring-[#484540]" />
                  ))}
                </div>
              )}

              {uploadStatus.error && (
                <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">{uploadStatus.error}</div>
              )}
              {uploadStatus.success && (
                <div className="mb-4 rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-300">Slides uploaded successfully!</div>
              )}

              <button type="submit" disabled={uploadStatus.loading} className={ui.primaryBtn}>
                {uploadStatus.loading ? 'Uploading…' : (<><Send className="h-4 w-4" /> Upload Slides</>)}
              </button>
            </form>

            {/* All uploaded slides, grouped by exhibition */}
            <div className="mt-10">
              <h3 className="text-sm font-semibold text-[#8a8175] uppercase tracking-wide mb-4">All Uploaded Slides</h3>

              {slides.length === 0 ? (
                <p className="text-sm text-[#6f675c]">No slides uploaded yet.</p>
              ) : (
                <div className="space-y-8">
                  {Object.entries(
                    slides.reduce((acc, s) => {
                      const key = s.brandName || 'Unknown exhibition';
                      (acc[key] = acc[key] || []).push(s);
                      return acc;
                    }, {})
                  ).map(([groupName, groupSlides]) => (
                    <div key={groupName}>
                      <p className="text-[#b7a78b] text-sm font-semibold uppercase tracking-wide mb-3">
                        {groupName} <span className="text-[#6f675c]">({groupSlides.length})</span>
                      </p>
                      <div className="space-y-4">
                        {groupSlides.map((slide) => {
                          const isEditing = editingSlideId === slide._id;
                          return (
                            <div key={slide._id} className="flex flex-col gap-4 rounded-xl border border-[#484540] bg-[#252525] p-4 sm:flex-row">
                              <div className="sm:w-40 shrink-0">
                                <img
                                  src={isEditing && slideEditPreview ? slideEditPreview : slide.image}
                                  alt=""
                                  className="h-28 w-full rounded-lg object-cover ring-1 ring-[#484540]"
                                />
                                {isEditing && (
                                  <label className="mt-2 inline-block cursor-pointer text-xs font-semibold text-[#8a8175] hover:text-[#b7a78b]">
                                    Replace image
                                    <input type="file" accept="image/*" onChange={handleSlideEditFile} className="hidden" />
                                  </label>
                                )}
                              </div>

                              <div className="min-w-0 flex-1">
                                {isEditing ? (
                                  <div className="space-y-3">
                                    <input type="text" value={slideEdit.text1} onChange={(e) => setSlideEdit((p) => ({ ...p, text1: e.target.value }))} placeholder="First text" className={ui.input} />
                                    <input type="text" value={slideEdit.text2} onChange={(e) => setSlideEdit((p) => ({ ...p, text2: e.target.value }))} placeholder="Second text" className={ui.input} />
                                    <input type="url" value={slideEdit.link} onChange={(e) => setSlideEdit((p) => ({ ...p, link: e.target.value }))} placeholder="Link" className={ui.input} />
                                    <div className="flex gap-2">
                                      <button className={ui.primaryBtn} onClick={() => handleSaveSlide(slide._id)} disabled={savingSlide}>
                                        {savingSlide ? 'Saving…' : 'Save'}
                                      </button>
                                      <button className={ui.editBtn} onClick={cancelEditSlide} disabled={savingSlide}>Cancel</button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <p className="text-[#e7ddd3] font-semibold uppercase tracking-wide text-sm">{slide.text1 || <span className="text-[#6f675c] normal-case">No first text</span>}</p>
                                    <p className="text-[#cdc2ad] text-sm mt-0.5">{slide.text2}</p>
                                    {slide.link && (
                                      <a href={slide.link} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-[#b7a78b] hover:underline break-all">{slide.link}</a>
                                    )}
                                    <div className="mt-3 flex gap-2">
                                      <button className={ui.editBtn} onClick={() => startEditSlide(slide)}>Edit</button>
                                      <button className={ui.deleteBtn} onClick={() => handleDeleteSlide(slide._id)}>Delete</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── EXHIBITION DETAILS ─────────────────────────────────────── */}
          <div className={ui.card}>
            <h2 className={ui.title}><Info className="h-5 w-5 text-[#b7a78b]" /> Exhibition Details</h2>
            <p className={ui.subtitle}>
              Title &amp; kind, where and when it showed, what it&apos;s about, and credits.
              These appear under the write-up on the exhibition page.
            </p>

            {/* Pick the exhibition right here — no need to scroll back up */}
            <div className="mb-6 max-w-md">
              <label className={ui.label}>Exhibition</label>
              {exhibitionSelect('details-exhibition')}
            </div>

            {!selectedExhibitionsId ? (
              <p className="text-sm text-[#6f675c]">Choose an exhibition above to add its details.</p>
            ) : (
              <>
                <div className="max-w-3xl">
                  <label className={ui.label}>Title &amp; kind</label>
                  <input
                    className={`${ui.input} mb-5`}
                    type="text"
                    placeholder="In Her Hands: Solo Exhibition"
                    value={info.titleKind}
                    onChange={(e) => setInfo({ ...info, titleKind: e.target.value })}
                  />

                  {/* Showings — an exhibition can travel, so these repeat */}
                  <label className={ui.label}>Location &amp; date</label>
                  <div className="space-y-3 mb-3">
                    {info.showings.map((s, i) => (
                      <div key={i} className="grid gap-3 sm:grid-cols-[1fr_220px_auto] sm:items-center">
                        <input
                          className={ui.input}
                          type="text"
                          placeholder="Our Urban Oasis · London, United Kingdom"
                          value={s.location}
                          onChange={(e) => updateShowing(i, 'location', e.target.value)}
                        />
                        <input
                          className={ui.input}
                          type="text"
                          placeholder="6-7 December 2025"
                          value={s.date}
                          onChange={(e) => updateShowing(i, 'date', e.target.value)}
                        />
                        <button
                          type="button"
                          className={ui.deleteBtn}
                          onClick={() => removeShowing(i)}
                          title="Remove this location"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>

                  <button type="button" className={`${ui.editBtn} mb-6`} onClick={addShowing}>
                    + Add another location
                  </button>

                  <label className={ui.label}>Overview</label>
                  <textarea
                    className={`${ui.input} mb-5 min-h-[130px] resize-y`}
                    placeholder="What the exhibition is about…"
                    value={info.overview}
                    onChange={(e) => setInfo({ ...info, overview: e.target.value })}
                  />

                  <label className={ui.label}>Credits</label>
                  <textarea
                    className={`${ui.input} min-h-[100px] resize-y`}
                    placeholder="Curator, production, partners…"
                    value={info.credits}
                    onChange={(e) => setInfo({ ...info, credits: e.target.value })}
                  />
                </div>

                <div className="mt-6 flex flex-wrap gap-3">
                  <button className={ui.primaryBtn} onClick={saveInfo} disabled={savingInfo}>
                    {savingInfo ? 'Saving…' : 'Save Details'}
                  </button>
                  {infoDocId && (
                    <button className={ui.deleteBtn} onClick={deleteInfo}>Delete Details</button>
                  )}
                </div>
              </>
            )}

            {/* All saved details, across every exhibition */}
            <div className="mt-10">
              <h3 className="text-sm font-semibold text-[#8a8175] uppercase tracking-wide mb-3">All Exhibition Details</h3>

              {allInfos.length === 0 ? (
                <p className="text-sm text-[#6f675c]">No exhibition details saved yet.</p>
              ) : (
                <div className="space-y-4">
                  {allInfos.map((i) => (
                    <div key={i._id} className="rounded-xl border border-[#484540] bg-[#252525] p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                        <div className="min-w-0 flex-1">
                          <p className="text-[#b7a78b] text-xs font-semibold uppercase tracking-wide mb-1">
                            {i.exhibitionName || 'Unknown exhibition'}
                          </p>

                          {i.titleKind && (
                            <p className="text-[#e7ddd3] text-[15px] font-semibold">{i.titleKind}</p>
                          )}

                          {i.showings?.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {i.showings.map((s, si) => (
                                <p key={si} className="text-[13px] text-[#cdc2ad]">
                                  {s.location}
                                  {s.location && s.date ? ' — ' : ''}
                                  <span className="text-[#8a8175]">{s.date}</span>
                                </p>
                              ))}
                            </div>
                          )}

                          {i.overview && (
                            <p className="mt-2 text-[13px] text-[#8a8175] line-clamp-2">{i.overview}</p>
                          )}
                        </div>

                        <div className="flex shrink-0 gap-2">
                          <button className={ui.editBtn} onClick={() => editInfoFromList(i)}>Edit</button>
                          <button className={ui.deleteBtn} onClick={() => deleteInfoById(i._id)}>Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── GALLERY IMAGES ─────────────────────────────────────────── */}
          <div className={ui.card}>
            <h2 className={ui.title}><ImageIcon className="h-5 w-5 text-[#b7a78b]" /> Gallery Images</h2>
            <p className={ui.subtitle}>The image grid shown below the write-up on the exhibition page.</p>

            <label htmlFor="exhibition-files" className={`${ui.dropzone} py-10`}>
              <ImageIcon className="mb-2 h-8 w-8 text-[#8a8175]" />
              <span className="text-sm font-semibold text-[#cdc2ad]">Click to choose images</span>
              <span className="mt-1 text-xs text-[#6f675c]">You can select multiple files (up to 10)</span>
              <input id="exhibition-files" type="file" onChange={handleImageChange} className="hidden" multiple accept="image/*" />
            </label>

            {images.length > 0 && (
              <div className="mt-5 grid grid-cols-3 gap-4 sm:grid-cols-4">
                {imagePreviews.map((preview, index) => (
                  <img key={index} src={preview} alt={`Selected ${index + 1}`} className="h-24 w-full rounded-lg object-cover ring-1 ring-[#484540]" />
                ))}
              </div>
            )}

            <button className={`${ui.primaryBtn} mt-6`} onClick={handleUpload} disabled={loading}>
              {loading ? 'Uploading…' : 'Upload Images'}
            </button>

            {/* Uploaded gallery images */}
            <div className="mt-10">
              <h3 className="text-sm font-semibold text-[#8a8175] uppercase tracking-wide mb-3">Uploaded Images</h3>
              <div className="overflow-x-auto rounded-lg border border-[#484540]">
                <table className="min-w-full divide-y divide-[#484540]">
                  <thead>
                    <tr>
                      <th className={ui.th}>S/N</th>
                      <th className={ui.th}>Exhibition</th>
                      <th className={ui.th}>Image</th>
                      <th className={ui.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#484540]">
                    {uploadedImages.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-10 text-center text-sm text-[#6f675c]">No images uploaded yet.</td>
                      </tr>
                    ) : (
                      uploadedImages.map((image, index) => (
                        <tr className={ui.tr} key={image.id || index}>
                          <td className={ui.td}>{index + 1}</td>
                          <td className={`${ui.td} capitalize text-[#e7ddd3]`}>{image.brandName || image.exhibitionName}</td>
                          <td className="px-6 py-4">
                            <img src={image.image} alt="" className="h-14 w-14 rounded-lg object-cover ring-1 ring-[#484540]" />
                          </td>
                          <td className="px-6 py-4">
                            <button className={ui.deleteBtn} onClick={() => handleDeleteExhibition(image.id)}>Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExhibitionUploadForm;
